import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { driversAPI, usersAPI, depotsAPI } from '../services/api';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  Users, 
  Search,
  Loader2,
  Calendar,
  Mail,
  AlertTriangle,
  Plus,
  X,
  Eye,
  Upload,
  FileText,
  HeartPulse,
  Shield,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  FileSpreadsheet
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DriverCard = ({ driver, onClick, onDelete, onToggleActive, isAdmin }) => {
  const isExpiringSoon = driver.license_expiry && 
    new Date(driver.license_expiry) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
  const isExpired = driver.license_expiry && 
    new Date(driver.license_expiry) < new Date();
  const isInactive = driver.is_active === false || driver.status === 'inactive' || driver.status === 'deleted';

  const driverName = driver.user 
    ? `${driver.user.first_name} ${driver.user.last_name}`
    : `${driver.first_name || ''} ${driver.last_name || ''}`.trim() || 'Unknown';

  const initials = driverName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  return (
    <Card 
      className={`cursor-pointer hover:shadow-md transition-all ${isInactive ? 'opacity-60 border-dashed' : ''}`}
      onClick={onClick}
      data-testid={`driver-card-${driver.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-lg font-semibold text-slate-600">
              {initials}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-semibold text-slate-900">
                {driverName}
              </span>
              {isInactive ? (
                <Badge className="bg-slate-200 text-slate-700">Inactive</Badge>
              ) : isExpired ? (
                <Badge className="bg-red-100 text-red-800">License Expired</Badge>
              ) : isExpiringSoon ? (
                <Badge className="bg-amber-100 text-amber-800">License Expiring</Badge>
              ) : (
                <Badge className="bg-green-100 text-green-800">Active</Badge>
              )}
              {driver.has_chronic_condition && (
                <Badge className="bg-purple-100 text-purple-800">
                  <HeartPulse className="w-3 h-3 mr-1" />
                  Chronic
                </Badge>
              )}
              {driver.ppe_issued && (
                <Badge className="bg-blue-100 text-blue-800">
                  <Shield className="w-3 h-3 mr-1" />
                  PPE
                </Badge>
              )}
              {isAdmin && (
                <div className="ml-auto flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleActive?.(driver); }}
                    className={`text-xs font-semibold px-2 py-1 rounded border transition-colors ${isInactive ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                    title={isInactive ? 'Re-activate driver' : 'Mark driver inactive'}
                    data-testid={`toggle-active-driver-${driver.id}`}
                  >
                    {isInactive ? 'Activate' : 'Deactivate'}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDelete(driver); }}
                    className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                    title="Remove Driver"
                    data-testid={`delete-driver-${driver.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <p className="text-sm text-slate-500 font-mono">{driver.employee_number}</p>
            <div className="flex items-center gap-4 mt-2 text-sm text-slate-500 flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                License: {driver.license_number}
              </span>
              {driver.license_expiry && (
                <span className={isExpired ? 'text-red-600' : isExpiringSoon ? 'text-amber-600' : ''}>
                  Exp: {new Date(driver.license_expiry).toLocaleDateString()}
                </span>
              )}
            </div>
            {(driver.user?.email || driver.email) && (
              <div className="flex items-center gap-1 mt-1 text-sm text-slate-500">
                <Mail className="w-4 h-4" />
                {driver.user?.email || driver.email}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const emptyDriver = {
  user_id: '',
  employee_number: '',
  license_number: '',
  license_expiry: '',
  prdp_number: '',
  prdp_expiry: '',
  medical_expiry: '',
  depot_id: '',
  supervisor_id: '',
  // Chronic condition fields
  has_chronic_condition: false,
  chronic_condition_description: '',
  chronic_condition_management: '',
};

const emptyWellbeing = {
  general_health_status: 'good',
  general_health_notes: '',
  condition_managed_properly: true,
  medication_taken_as_prescribed: true,
  condition_management_notes: '',
  feels_alert: true,
  had_sufficient_rest: true,
  alertness_notes: '',
  sleep_hours_last_night: 7,
  sleep_quality: 'good',
  sleep_notes: '',
  mental_state: 'good',
  stress_level: 'low',
  mental_wellbeing_notes: '',
  supervisor_observation: '',
  supervisor_recommends_driving: true,
  supervisor_notes: '',
  fitness_declaration: 'fit',
  declaration_notes: '',
};

const emptyPPE = {
  safety_shoes: false,
  hats: false,
  goggles: false,
  ear_plugs: false,
  overalls: false,
  gloves: false,
  reflectors: false,
  issue_date: new Date().toISOString().slice(0, 10),
  notes: '',
};

export const DriversPage = () => {
  const [searchParams] = useSearchParams();
  const { user, isSupervisor, isAdmin } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [users, setUsers] = useState([]);
  const [depots, setDepots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyDriver);
  const [activeTab, setActiveTab] = useState('details');
  
  // Document upload
  const fileInputRef = useRef(null);
  const [uploadType, setUploadType] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState({});

  const viewDocumentAuth = async (docPath) => {
    const newWindow = window.open('about:blank', '_blank');
    try {
      const res = await axios.get(`${API_URL}${docPath}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.data.type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      if (newWindow) {
        newWindow.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch {
      if (newWindow) newWindow.close();
      toast.error('Failed to open document');
    }
  };
  
  // Wellbeing
  const [wellbeingRecords, setWellbeingRecords] = useState([]);
  const [wellbeingForm, setWellbeingForm] = useState(emptyWellbeing);
  const [showWellbeingForm, setShowWellbeingForm] = useState(false);
  const [wellbeingStatus, setWellbeingStatus] = useState(null);
  
  // PPE
  const [ppeRecords, setPPERecords] = useState([]);
  const [ppeForm, setPPEForm] = useState(emptyPPE);
  const [showPPEForm, setShowPPEForm] = useState(false);
  const [ppeStatus, setPPEStatus] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const fetchDrivers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await driversAPI.list({ include_inactive: showInactive });
      setDrivers(response.data);
    } catch (error) {
      console.error('Failed to fetch drivers:', error);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchDrivers();

    // Check if adding new driver
    const addNew = searchParams.get('add');
    if (addNew === 'true') {
      setEditingDriver(null);
      setFormData(emptyDriver);
      setActiveTab('details');
      setShowDialog(true);
    }
  }, [searchParams, fetchDrivers]);

  // Fetch users and depots for the form
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const [usersRes, depotsRes] = await Promise.all([
          usersAPI.list(),
          depotsAPI.list()
        ]);
        setUsers(usersRes.data || []);
        setDepots(depotsRes.data || []);
      } catch (error) {
        console.error('Failed to fetch form data:', error);
      }
    };
    fetchFormData();
  }, []);

  // Fetch driver-specific data when editing
  useEffect(() => {
    const fetchDriverData = async () => {
      if (!editingDriver) return;
      
      try {
        // Fetch documents
        const docsRes = await driversAPI.listDocuments(editingDriver.id);
        setDocuments(docsRes.data || {});
        
        // Fetch wellbeing records
        const wellbeingRes = await driversAPI.listWellbeing(editingDriver.id);
        setWellbeingRecords(wellbeingRes.data || []);
        
        // Fetch wellbeing status
        const statusRes = await driversAPI.getWellbeingStatus(editingDriver.id);
        setWellbeingStatus(statusRes.data);
        
        // Fetch PPE records
        const ppeRes = await driversAPI.listPPE(editingDriver.id);
        setPPERecords(ppeRes.data || []);
        
        // Fetch PPE status
        const ppeStatusRes = await driversAPI.getPPEStatus(editingDriver.id);
        setPPEStatus(ppeStatusRes.data);
      } catch (error) {
        console.error('Failed to fetch driver data:', error);
      }
    };
    
    fetchDriverData();
  }, [editingDriver]);

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      user_id: driver.user_id || '',
      employee_number: driver.employee_number || '',
      license_number: driver.license_number || '',
      license_expiry: driver.license_expiry?.slice(0, 10) || '',
      prdp_number: driver.prdp_number || '',
      prdp_expiry: driver.prdp_expiry?.slice(0, 10) || '',
      medical_expiry: driver.medical_expiry?.slice(0, 10) || '',
      depot_id: driver.depot_id || depots[0]?.id || '',
      supervisor_id: driver.supervisor_id || '',
      has_chronic_condition: driver.has_chronic_condition || false,
      chronic_condition_description: driver.chronic_condition_description || '',
      chronic_condition_management: driver.chronic_condition_management || '',
    });
    setActiveTab('details');
    setShowDialog(true);
  };

  const handleAdd = () => {
    setEditingDriver(null);
    setFormData({
      ...emptyDriver,
      depot_id: depots[0]?.id || ''
    });
    setActiveTab('details');
    setShowDialog(true);
  };

  const handleDeleteDriver = async (driver) => {
    const name = `${driver.first_name || driver.user?.first_name || ''} ${driver.last_name || driver.user?.last_name || ''}`.trim() || driver.employee_number;
    if (!window.confirm(`Are you sure you want to remove ${name}? This will deactivate the driver record.`)) return;
    try {
      await driversAPI.delete(driver.id);
      toast.success(`${name} removed`);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove driver');
    }
  };

  const handleToggleDriverActive = async (driver) => {
    const name = `${driver.first_name || driver.user?.first_name || ''} ${driver.last_name || driver.user?.last_name || ''}`.trim() || driver.employee_number;
    const isCurrentlyInactive = driver.is_active === false || driver.status === 'inactive' || driver.status === 'deleted';
    const action = isCurrentlyInactive ? 'activate' : 'deactivate';
    const confirmMsg = isCurrentlyInactive
      ? `Re-activate ${name}?`
      : `Deactivate ${name}? They will be hidden from active driver lists but the record remains intact.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      if (isCurrentlyInactive) {
        await driversAPI.restore(driver.id);
      } else {
        await driversAPI.delete(driver.id);
      }
      toast.success(`${name} ${action}d`);
      fetchDrivers();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} driver`);
    }
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    
    if (!formData.employee_number || !formData.license_number || 
        !formData.license_expiry || !formData.depot_id || !formData.supervisor_id) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        license_expiry: new Date(formData.license_expiry).toISOString(),
        prdp_expiry: formData.prdp_expiry ? new Date(formData.prdp_expiry).toISOString() : null,
        medical_expiry: formData.medical_expiry ? new Date(formData.medical_expiry).toISOString() : null,
      };
      
      let savedDriver = null;
      if (editingDriver) {
        await driversAPI.update(editingDriver.id, payload);
        toast.success('Driver updated successfully');
        
        // Update chronic condition separately
        if (formData.has_chronic_condition !== editingDriver.has_chronic_condition ||
            formData.chronic_condition_description !== editingDriver.chronic_condition_description ||
            formData.chronic_condition_management !== editingDriver.chronic_condition_management) {
          await driversAPI.updateChronicCondition(
            editingDriver.id,
            {
              has_chronic_condition: formData.has_chronic_condition,
              chronic_condition_description: formData.chronic_condition_description,
              chronic_condition_management: formData.chronic_condition_management,
            },
            user.id,
            `${user.first_name} ${user.last_name}`
          );
        }
      } else {
        // For new driver, user_id is required
        if (!payload.user_id) {
          toast.error('Please select a user account');
          setSubmitting(false);
          return;
        }
        const response = await driversAPI.create(payload);
        savedDriver = response.data;
        toast.success('Driver added! You can now manage documents and PPE.');
      }
      
      // Refresh drivers list
      const response = await driversAPI.list();
      setDrivers(response.data);
      
      // If this was a new driver, re-open in edit mode with Documents tab
      if (savedDriver) {
        const newDriver = response.data.find(d => d.id === savedDriver.id);
        if (newDriver) {
          setEditingDriver(newDriver);
          setFormData({
            user_id: newDriver.user_id || '',
            employee_number: newDriver.employee_number || '',
            license_number: newDriver.license_number || '',
            license_expiry: newDriver.license_expiry?.slice(0, 10) || '',
            prdp_number: newDriver.prdp_number || '',
            prdp_expiry: newDriver.prdp_expiry?.slice(0, 10) || '',
            medical_expiry: newDriver.medical_expiry?.slice(0, 10) || '',
            depot_id: newDriver.depot_id || '',
            supervisor_id: newDriver.supervisor_id || '',
            has_chronic_condition: newDriver.has_chronic_condition || false,
            chronic_condition_description: newDriver.chronic_condition_description || '',
            chronic_condition_management: newDriver.chronic_condition_management || '',
          });
          setActiveTab('documents');
        } else {
          setShowDialog(false);
        }
      }
    } catch (error) {
      console.error('Failed to save driver:', error);
      toast.error(error.response?.data?.detail || 'Failed to save driver');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingDriver || !uploadType) return;

    try {
      setUploading(true);
      await driversAPI.uploadDocument(editingDriver.id, file, uploadType);
      toast.success('Document uploaded successfully');
      
      // Refresh documents
      const docsRes = await driversAPI.listDocuments(editingDriver.id);
      setDocuments(docsRes.data || {});
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
      setUploadType(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const triggerUpload = (type) => {
    setUploadType(type);
    fileInputRef.current?.click();
  };

  const handleDeleteDoc = async (doc) => {
    if (!editingDriver) return;
    if (!window.confirm(`Delete "${doc.filename}"? This cannot be undone.`)) return;
    try {
      await driversAPI.deleteDocument(editingDriver.id, doc.id);
      toast.success('Document deleted');
      const docsRes = await driversAPI.listDocuments(editingDriver.id);
      setDocuments(docsRes.data || {});
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleWellbeingSubmit = async () => {
    if (!editingDriver) return;
    
    try {
      setSubmitting(true);
      await driversAPI.createWellbeing(
        editingDriver.id,
        wellbeingForm,
        user.id,
        `${user.first_name} ${user.last_name}`
      );
      
      toast.success('Wellbeing questionnaire submitted');
      setShowWellbeingForm(false);
      setWellbeingForm(emptyWellbeing);
      
      // Refresh data
      const wellbeingRes = await driversAPI.listWellbeing(editingDriver.id);
      setWellbeingRecords(wellbeingRes.data || []);
      const statusRes = await driversAPI.getWellbeingStatus(editingDriver.id);
      setWellbeingStatus(statusRes.data);
    } catch (error) {
      console.error('Failed to submit wellbeing:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit questionnaire');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePPESubmit = async () => {
    if (!editingDriver) return;
    
    // Check if at least one PPE item is selected
    const hasItems = ppeForm.safety_shoes || ppeForm.hats || ppeForm.goggles || 
                     ppeForm.ear_plugs || ppeForm.overalls || ppeForm.gloves || ppeForm.reflectors;
    if (!hasItems) {
      toast.error('Please select at least one PPE item');
      return;
    }
    
    try {
      setSubmitting(true);
      await driversAPI.createPPE(
        editingDriver.id,
        {
          ...ppeForm,
          driver_id: editingDriver.id,
          issue_date: new Date(ppeForm.issue_date).toISOString(),
        },
        user.id,
        `${user.first_name} ${user.last_name}`
      );
      
      toast.success('PPE record created');
      setShowPPEForm(false);
      setPPEForm(emptyPPE);
      
      // Refresh data
      const ppeRes = await driversAPI.listPPE(editingDriver.id);
      setPPERecords(ppeRes.data || []);
      const ppeStatusRes = await driversAPI.getPPEStatus(editingDriver.id);
      setPPEStatus(ppeStatusRes.data);
      
      // Refresh driver list to show PPE badge
      const response = await driversAPI.list();
      setDrivers(response.data);
      const updatedDriver = response.data.find(d => d.id === editingDriver.id);
      if (updatedDriver) setEditingDriver(updatedDriver);
    } catch (error) {
      console.error('Failed to create PPE record:', error);
      toast.error(error.response?.data?.detail || 'Failed to create PPE record');
    } finally {
      setSubmitting(false);
    }
  };

  // Get users who aren't already drivers
  const existingDriverUserIds = drivers.map(d => d.user_id).filter(Boolean);
  const availableUsers = users.filter(u => !existingDriverUserIds.includes(u.id));
  
  // Get supervisors for the dropdown
  const supervisors = users.filter(u => 
    u.role === 'supervisor' || u.role === 'admin' || u.role === 'management'
  );

  const filteredDrivers = drivers.filter(d => {
    const searchLower = searchQuery.toLowerCase();
    const driverName = d.user 
      ? `${d.user.first_name} ${d.user.last_name}`
      : `${d.first_name || ''} ${d.last_name || ''}`;
    return driverName.toLowerCase().includes(searchLower) ||
           (d.employee_number || '').toLowerCase().includes(searchLower) ||
           (d.license_number || '').toLowerCase().includes(searchLower);
  });

  // Stats
  const totalDrivers = drivers.length;
  const expiringCount = drivers.filter(d => {
    if (!d.license_expiry) return false;
    const expiry = new Date(d.license_expiry);
    const threshold = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    return expiry < threshold && expiry > new Date();
  }).length;
  const expiredCount = drivers.filter(d => 
    d.license_expiry && new Date(d.license_expiry) < new Date()
  ).length;
  const chronicCount = drivers.filter(d => d.has_chronic_condition).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="drivers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            DRIVERS
          </h1>
          <p className="text-slate-500 mt-1">
            Manage your driver roster, health & PPE
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-600 select-none" data-testid="show-inactive-drivers-label">
            <Switch
              checked={showInactive}
              onCheckedChange={setShowInactive}
              data-testid="show-inactive-drivers-toggle"
            />
            Show inactive
          </label>
          <Button 
            variant="outline" 
            onClick={() => driversAPI.exportExcel()} 
            className="gap-2"
            data-testid="export-drivers-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
          {(isSupervisor || isAdmin) && (
            <Button 
              onClick={handleAdd}
              className="gap-2"
              data-testid="add-driver-btn"
            >
              <Plus className="w-4 h-4" />
              Add Driver
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
              <Users className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 font-mono">{totalDrivers}</p>
              <p className="text-sm text-slate-500">Total Drivers</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600 font-mono">{expiringCount}</p>
              <p className="text-sm text-slate-500">License Expiring</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-sm flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600 font-mono">{expiredCount}</p>
              <p className="text-sm text-slate-500">Expired</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-sm flex items-center justify-center">
              <HeartPulse className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600 font-mono">{chronicCount}</p>
              <p className="text-sm text-slate-500">Chronic Conditions</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          placeholder="Search by name, employee number, or license..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 h-12"
          data-testid="drivers-search"
        />
      </div>

      {/* Drivers Grid */}
      {filteredDrivers.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No drivers found</h3>
          <p className="text-slate-500 mt-1">
            {searchQuery ? 'Try adjusting your search' : 'No driver profiles have been created yet'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredDrivers.map((driver) => (
            <DriverCard 
              key={driver.id} 
              driver={driver}
              onClick={() => handleEdit(driver)}
              onDelete={handleDeleteDriver}
              onToggleActive={handleToggleDriverActive}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingDriver ? 'DRIVER DETAILS' : 'ADD NEW DRIVER'}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="documents" disabled={!editingDriver}>Documents</TabsTrigger>
              <TabsTrigger value="wellbeing" disabled={!editingDriver}>Wellbeing</TabsTrigger>
              <TabsTrigger value="ppe" disabled={!editingDriver}>PPE</TabsTrigger>
            </TabsList>

            {/* DETAILS TAB */}
            <TabsContent value="details" className="space-y-4 mt-4">
              {!editingDriver && (
                <div className="space-y-2">
                  <Label htmlFor="user_id">User Account *</Label>
                  <Select 
                    value={formData.user_id} 
                    onValueChange={(v) => setFormData({...formData, user_id: v})}
                  >
                    <SelectTrigger data-testid="user-select">
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {availableUsers.length === 0 && (
                    <p className="text-xs text-amber-600">No available users. Create a user account first.</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="employee_number">Employee Number *</Label>
                  <Input
                    id="employee_number"
                    value={formData.employee_number}
                    onChange={(e) => setFormData({...formData, employee_number: e.target.value})}
                    placeholder="EMP001"
                    data-testid="employee-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="license_number">License Number *</Label>
                  <Input
                    id="license_number"
                    value={formData.license_number}
                    onChange={(e) => setFormData({...formData, license_number: e.target.value})}
                    placeholder="DL12345678"
                    data-testid="license-number-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="license_expiry">License Expiry *</Label>
                  <Input
                    id="license_expiry"
                    type="date"
                    value={formData.license_expiry}
                    onChange={(e) => setFormData({...formData, license_expiry: e.target.value})}
                    data-testid="license-expiry-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prdp_number">PrDP Number</Label>
                  <Input
                    id="prdp_number"
                    value={formData.prdp_number}
                    onChange={(e) => setFormData({...formData, prdp_number: e.target.value})}
                    placeholder="Optional"
                    data-testid="prdp-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prdp_expiry">PrDP Expiry</Label>
                  <Input
                    id="prdp_expiry"
                    type="date"
                    value={formData.prdp_expiry}
                    onChange={(e) => setFormData({...formData, prdp_expiry: e.target.value})}
                    data-testid="prdp-expiry-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="medical_expiry">Medical Expiry</Label>
                  <Input
                    id="medical_expiry"
                    type="date"
                    value={formData.medical_expiry}
                    onChange={(e) => setFormData({...formData, medical_expiry: e.target.value})}
                    data-testid="medical-expiry-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="depot_id">Depot *</Label>
                  <Select 
                    value={formData.depot_id} 
                    onValueChange={(v) => setFormData({...formData, depot_id: v})}
                  >
                    <SelectTrigger data-testid="depot-select">
                      <SelectValue placeholder="Select depot" />
                    </SelectTrigger>
                    <SelectContent>
                      {depots.map(depot => (
                        <SelectItem key={depot.id} value={depot.id}>
                          {depot.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="supervisor_id">Supervisor *</Label>
                  <Select 
                    value={formData.supervisor_id} 
                    onValueChange={(v) => setFormData({...formData, supervisor_id: v})}
                  >
                    <SelectTrigger data-testid="supervisor-select">
                      <SelectValue placeholder="Select supervisor" />
                    </SelectTrigger>
                    <SelectContent>
                      {supervisors.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Chronic Condition Section */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <HeartPulse className="w-4 h-4" />
                  Chronic Condition & Wellbeing
                </h4>
                
                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="has_chronic"
                    checked={formData.has_chronic_condition}
                    onCheckedChange={(checked) => setFormData({...formData, has_chronic_condition: checked})}
                    data-testid="chronic-condition-checkbox"
                  />
                  <Label htmlFor="has_chronic" className="cursor-pointer">
                    Driver has a chronic condition requiring monitoring
                  </Label>
                </div>

                {formData.has_chronic_condition && (
                  <div className="space-y-4 pl-6 border-l-2 border-purple-200">
                    <div className="space-y-2">
                      <Label>Condition Description</Label>
                      <Textarea
                        value={formData.chronic_condition_description}
                        onChange={(e) => setFormData({...formData, chronic_condition_description: e.target.value})}
                        placeholder="Describe the chronic condition (e.g., Diabetes Type 2, Hypertension)"
                        data-testid="chronic-description-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>How is the condition managed?</Label>
                      <Textarea
                        value={formData.chronic_condition_management}
                        onChange={(e) => setFormData({...formData, chronic_condition_management: e.target.value})}
                        placeholder="Describe medication, monitoring, lifestyle changes, etc."
                        data-testid="chronic-management-input"
                      />
                    </div>
                    <div className="bg-purple-50 p-3 rounded-md text-sm text-purple-700">
                      <strong>Note:</strong> A Truck Driver Wellbeing Check (Pre-Medical Screening) questionnaire must be completed every 6 months for drivers with chronic conditions.
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowDialog(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={submitting}
                  data-testid="submit-driver-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    editingDriver ? 'Update Driver' : 'Add Driver'
                  )}
                </Button>
              </div>
            </TabsContent>

            {/* DOCUMENTS TAB */}
            <TabsContent value="documents" className="space-y-4 mt-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleUpload}
                className="hidden"
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
              />
              
              <div className="grid grid-cols-2 gap-4">
                {/* Driver License */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Driver License
                    </Label>
                    <Button size="sm" variant="outline" onClick={() => triggerUpload('license')} disabled={uploading}>
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                  {documents.license?.length > 0 ? (
                    <div className="space-y-1 mt-2">
                      {documents.license.map((doc, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            onClick={() => viewDocumentAuth(doc.path)}
                            className="flex-1 flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer text-left"
                          >
                            <Eye className="w-3 h-3" />
                            {doc.filename}
                          </button>
                          {isAdmin && (
                            <button onClick={() => handleDeleteDoc(doc)} className="text-red-500 hover:text-red-700" title="Delete">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No files uploaded</div>
                  )}
                </Card>

                {/* Medical Certificate */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Medical Certificate
                    </Label>
                    <Button size="sm" variant="outline" onClick={() => triggerUpload('medical_certificate')} disabled={uploading}>
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                  {documents.medical_certificate?.length > 0 ? (
                    <div className="space-y-1 mt-2">
                      {documents.medical_certificate.map((doc, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            onClick={() => viewDocumentAuth(doc.path)}
                            className="flex-1 flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer text-left"
                          >
                            <Eye className="w-3 h-3" />
                            {doc.filename}
                          </button>
                          {isAdmin && (
                            <button onClick={() => handleDeleteDoc(doc)} className="text-red-500 hover:text-red-700" title="Delete">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No files uploaded</div>
                  )}
                </Card>

                {/* PPE Acknowledgement */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      PPE Acknowledgement
                    </Label>
                    <Button size="sm" variant="outline" onClick={() => triggerUpload('ppe_acknowledgement')} disabled={uploading}>
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                  {documents.ppe_acknowledgement?.length > 0 ? (
                    <div className="space-y-1 mt-2">
                      {documents.ppe_acknowledgement.map((doc, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            onClick={() => viewDocumentAuth(doc.path)}
                            className="flex-1 flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer text-left"
                          >
                            <Eye className="w-3 h-3" />
                            {doc.filename}
                          </button>
                          {isAdmin && (
                            <button onClick={() => handleDeleteDoc(doc)} className="text-red-500 hover:text-red-700" title="Delete">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No files uploaded</div>
                  )}
                </Card>

                {/* Other Documents */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Other Documents
                    </Label>
                    <Button size="sm" variant="outline" onClick={() => triggerUpload('other')} disabled={uploading}>
                      <Upload className="w-4 h-4 mr-1" />
                      Upload
                    </Button>
                  </div>
                  {documents.other?.length > 0 ? (
                    <div className="space-y-1 mt-2">
                      {documents.other.map((doc, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            onClick={() => viewDocumentAuth(doc.path)}
                            className="flex-1 flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer text-left"
                          >
                            <Eye className="w-3 h-3" />
                            {doc.filename}
                          </button>
                          {isAdmin && (
                            <button onClick={() => handleDeleteDoc(doc)} className="text-red-500 hover:text-red-700" title="Delete">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No files uploaded</div>
                  )}
                </Card>
              </div>

              {uploading && (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  <span className="ml-2 text-slate-500">Uploading...</span>
                </div>
              )}
            </TabsContent>

            {/* WELLBEING TAB */}
            <TabsContent value="wellbeing" className="space-y-4 mt-4">
              {!editingDriver?.has_chronic_condition ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <HeartPulse className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700">No Chronic Condition Recorded</h3>
                  <p className="text-slate-500 mt-1">
                    Wellbeing questionnaires are only required for drivers with chronic conditions.
                  </p>
                  <p className="text-slate-500 mt-1 text-sm">
                    Go to the Details tab to record a chronic condition.
                  </p>
                </div>
              ) : (
                <>
                  {/* Status Card */}
                  {wellbeingStatus && (
                    <Card className={`p-4 ${wellbeingStatus.is_overdue ? 'bg-red-50 border-red-200' : wellbeingStatus.days_until_due <= 30 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold">Wellbeing Check Status</h4>
                          {wellbeingStatus.is_overdue ? (
                            <p className="text-red-600">Overdue! Last completed: {wellbeingStatus.last_completed ? new Date(wellbeingStatus.last_completed).toLocaleDateString() : 'Never'}</p>
                          ) : (
                            <p className={wellbeingStatus.days_until_due <= 30 ? 'text-amber-600' : 'text-green-600'}>
                              Next due: {wellbeingStatus.next_due ? new Date(wellbeingStatus.next_due).toLocaleDateString() : 'Not set'} ({wellbeingStatus.days_until_due} days)
                            </p>
                          )}
                        </div>
                        <Button onClick={() => setShowWellbeingForm(true)} data-testid="new-wellbeing-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          New Questionnaire
                        </Button>
                      </div>
                    </Card>
                  )}

                  {/* New Questionnaire Form */}
                  {showWellbeingForm && (
                    <Card className="p-4 bg-purple-50 border-purple-200">
                      <h4 className="font-semibold text-purple-800 mb-4">Truck Driver Wellbeing Check (Pre-Medical Screening)</h4>
                      
                      <div className="space-y-6">
                        {/* General Health */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">1. General Health</Label>
                          <Select 
                            value={wellbeingForm.general_health_status} 
                            onValueChange={(v) => setWellbeingForm({...wellbeingForm, general_health_status: v})}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="good">Good</SelectItem>
                              <SelectItem value="fair">Fair</SelectItem>
                              <SelectItem value="poor">Poor</SelectItem>
                            </SelectContent>
                          </Select>
                          <Textarea
                            placeholder="Additional notes about general health..."
                            value={wellbeingForm.general_health_notes}
                            onChange={(e) => setWellbeingForm({...wellbeingForm, general_health_notes: e.target.value})}
                            className="mt-2"
                          />
                        </div>

                        {/* Condition Management */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">2. Condition Management</Label>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="condition_managed"
                                checked={wellbeingForm.condition_managed_properly}
                                onCheckedChange={(checked) => setWellbeingForm({...wellbeingForm, condition_managed_properly: checked})}
                              />
                              <Label htmlFor="condition_managed">Condition managed properly</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="medication_taken"
                                checked={wellbeingForm.medication_taken_as_prescribed}
                                onCheckedChange={(checked) => setWellbeingForm({...wellbeingForm, medication_taken_as_prescribed: checked})}
                              />
                              <Label htmlFor="medication_taken">Medication taken as prescribed</Label>
                            </div>
                          </div>
                        </div>

                        {/* Alertness & Fitness */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">3. Alertness & Fitness to Drive</Label>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="feels_alert"
                                checked={wellbeingForm.feels_alert}
                                onCheckedChange={(checked) => setWellbeingForm({...wellbeingForm, feels_alert: checked})}
                              />
                              <Label htmlFor="feels_alert">Feels alert</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id="had_rest"
                                checked={wellbeingForm.had_sufficient_rest}
                                onCheckedChange={(checked) => setWellbeingForm({...wellbeingForm, had_sufficient_rest: checked})}
                              />
                              <Label htmlFor="had_rest">Had sufficient rest</Label>
                            </div>
                          </div>
                        </div>

                        {/* Sleep & Rest */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">4. Sleep & Rest</Label>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Hours of sleep last night</Label>
                              <Input
                                type="number"
                                min="0"
                                max="24"
                                step="0.5"
                                value={wellbeingForm.sleep_hours_last_night}
                                onChange={(e) => setWellbeingForm({...wellbeingForm, sleep_hours_last_night: parseFloat(e.target.value) || 0})}
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Sleep quality</Label>
                              <Select 
                                value={wellbeingForm.sleep_quality} 
                                onValueChange={(v) => setWellbeingForm({...wellbeingForm, sleep_quality: v})}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="good">Good</SelectItem>
                                  <SelectItem value="fair">Fair</SelectItem>
                                  <SelectItem value="poor">Poor</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        {/* Mental & Emotional */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">5. Mental & Emotional Wellbeing</Label>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Mental state</Label>
                              <Select 
                                value={wellbeingForm.mental_state} 
                                onValueChange={(v) => setWellbeingForm({...wellbeingForm, mental_state: v})}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="good">Good</SelectItem>
                                  <SelectItem value="fair">Fair</SelectItem>
                                  <SelectItem value="poor">Poor</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Stress level</Label>
                              <Select 
                                value={wellbeingForm.stress_level} 
                                onValueChange={(v) => setWellbeingForm({...wellbeingForm, stress_level: v})}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="low">Low</SelectItem>
                                  <SelectItem value="moderate">Moderate</SelectItem>
                                  <SelectItem value="high">High</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        {/* Supervisor Observation */}
                        <div className="space-y-2">
                          <Label className="text-sm font-semibold">6. Supervisor Observation</Label>
                          <Textarea
                            placeholder="Supervisor observations about driver's condition..."
                            value={wellbeingForm.supervisor_observation}
                            onChange={(e) => setWellbeingForm({...wellbeingForm, supervisor_observation: e.target.value})}
                          />
                          <div className="flex items-center space-x-2 mt-2">
                            <Checkbox
                              id="supervisor_recommends"
                              checked={wellbeingForm.supervisor_recommends_driving}
                              onCheckedChange={(checked) => setWellbeingForm({...wellbeingForm, supervisor_recommends_driving: checked})}
                            />
                            <Label htmlFor="supervisor_recommends">Supervisor recommends driver is fit to drive</Label>
                          </div>
                        </div>

                        {/* Final Declaration */}
                        <div className="space-y-2 bg-white p-4 rounded-lg border">
                          <Label className="text-sm font-semibold">7. Final Self-Declaration</Label>
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                id="fit"
                                name="fitness"
                                checked={wellbeingForm.fitness_declaration === 'fit'}
                                onChange={() => setWellbeingForm({...wellbeingForm, fitness_declaration: 'fit'})}
                              />
                              <Label htmlFor="fit" className="flex items-center gap-1 text-green-700">
                                <CheckCircle2 className="w-4 h-4" />
                                FIT TO DRIVE
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                id="not_fit"
                                name="fitness"
                                checked={wellbeingForm.fitness_declaration === 'not_fit'}
                                onChange={() => setWellbeingForm({...wellbeingForm, fitness_declaration: 'not_fit'})}
                              />
                              <Label htmlFor="not_fit" className="flex items-center gap-1 text-red-700">
                                <XCircle className="w-4 h-4" />
                                NOT FIT TO DRIVE
                              </Label>
                            </div>
                          </div>
                          <Textarea
                            placeholder="Additional declaration notes..."
                            value={wellbeingForm.declaration_notes}
                            onChange={(e) => setWellbeingForm({...wellbeingForm, declaration_notes: e.target.value})}
                            className="mt-2"
                          />
                        </div>

                        <div className="flex justify-end gap-3">
                          <Button variant="outline" onClick={() => setShowWellbeingForm(false)}>Cancel</Button>
                          <Button onClick={handleWellbeingSubmit} disabled={submitting} data-testid="submit-wellbeing-btn">
                            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Submit Questionnaire
                          </Button>
                        </div>
                      </div>
                    </Card>
                  )}

                  {/* History */}
                  <div>
                    <h4 className="font-semibold text-slate-700 mb-3">Questionnaire History</h4>
                    {wellbeingRecords.length === 0 ? (
                      <p className="text-slate-500 text-sm">No questionnaires completed yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {wellbeingRecords.map((record) => (
                          <Card key={record.id} className="p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{new Date(record.created_at).toLocaleDateString()}</p>
                                <p className="text-sm text-slate-500">By: {record.completed_by_name}</p>
                              </div>
                              <Badge className={record.fitness_declaration === 'fit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {record.fitness_declaration === 'fit' ? 'Fit' : 'Not Fit'}
                              </Badge>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </TabsContent>

            {/* PPE TAB */}
            <TabsContent value="ppe" className="space-y-4 mt-4">
              {/* Status Card */}
              {ppeStatus && (
                <Card className={`p-4 ${!ppeStatus.ppe_issued ? 'bg-red-50 border-red-200' : ppeStatus.is_overdue ? 'bg-red-50 border-red-200' : ppeStatus.days_until_due <= 30 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">PPE Status</h4>
                      {!ppeStatus.ppe_issued ? (
                        <p className="text-red-600">No PPE issued to this driver</p>
                      ) : ppeStatus.is_overdue ? (
                        <p className="text-red-600">PPE renewal overdue! Last issued: {new Date(ppeStatus.ppe_issue_date).toLocaleDateString()}</p>
                      ) : (
                        <p className={ppeStatus.days_until_due <= 30 ? 'text-amber-600' : 'text-green-600'}>
                          Next renewal: {new Date(ppeStatus.next_due).toLocaleDateString()} ({ppeStatus.days_until_due} days)
                        </p>
                      )}
                      {ppeStatus.ppe_items?.length > 0 && (
                        <p className="text-sm text-slate-500 mt-1">Items: {ppeStatus.ppe_items.join(', ')}</p>
                      )}
                    </div>
                    <Button onClick={() => setShowPPEForm(true)} data-testid="new-ppe-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Issue PPE
                    </Button>
                  </div>
                </Card>
              )}

              {/* New PPE Form */}
              {showPPEForm && (
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <h4 className="font-semibold text-blue-800 mb-4">Issue PPE to Driver</h4>
                  
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Issue Date *</Label>
                      <Input
                        type="date"
                        value={ppeForm.issue_date}
                        onChange={(e) => setPPEForm({...ppeForm, issue_date: e.target.value})}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">PPE Items Issued</Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { key: 'safety_shoes', label: 'Safety Shoes' },
                          { key: 'hats', label: 'Hats' },
                          { key: 'goggles', label: 'Goggles' },
                          { key: 'ear_plugs', label: 'Ear Plugs' },
                          { key: 'overalls', label: 'Overalls' },
                          { key: 'gloves', label: 'Gloves' },
                          { key: 'reflectors', label: 'Reflectors' },
                        ].map(item => (
                          <div key={item.key} className="flex items-center space-x-2">
                            <Checkbox
                              id={item.key}
                              checked={ppeForm[item.key]}
                              onCheckedChange={(checked) => setPPEForm({...ppeForm, [item.key]: checked})}
                            />
                            <Label htmlFor={item.key}>{item.label}</Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        placeholder="Additional notes about PPE issuance..."
                        value={ppeForm.notes}
                        onChange={(e) => setPPEForm({...ppeForm, notes: e.target.value})}
                      />
                    </div>

                    <div className="bg-blue-100 p-3 rounded-md text-sm text-blue-700">
                      <strong>Note:</strong> PPE must be renewed every 12 months. A corrective action will be created automatically when renewal is due.
                    </div>

                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onClick={() => setShowPPEForm(false)}>Cancel</Button>
                      <Button onClick={handlePPESubmit} disabled={submitting} data-testid="submit-ppe-btn">
                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Record PPE Issuance
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {/* History */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3">PPE Issuance History</h4>
                {ppeRecords.length === 0 ? (
                  <p className="text-slate-500 text-sm">No PPE records yet.</p>
                ) : (
                  <div className="space-y-2">
                    {ppeRecords.map((record) => (
                      <Card key={record.id} className="p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">Issued: {new Date(record.issue_date).toLocaleDateString()}</p>
                            <p className="text-sm text-slate-500">
                              Items: {[
                                record.safety_shoes && 'Safety Shoes',
                                record.hats && 'Hats',
                                record.goggles && 'Goggles',
                                record.ear_plugs && 'Ear Plugs',
                                record.overalls && 'Overalls',
                                record.gloves && 'Gloves',
                                record.reflectors && 'Reflectors',
                              ].filter(Boolean).join(', ')}
                            </p>
                            <p className="text-sm text-slate-500">By: {record.issued_by_name}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline">
                              Renewal: {new Date(record.next_ppe_due).toLocaleDateString()}
                            </Badge>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DriversPage;
